/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState, useContext, useEffect, useRef } from 'react';
import mbpLogger from 'mbp-logger';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from 'react-query';
import { useFormContext } from 'react-hook-form';
import { getSSRDeviceType } from '../../../../../../../../../state/ducks/App/App-Selectors';
import { object, bool, string, func, arrayOf, PropTypes } from 'prop-types';

// MUI
import { Button, CircularProgress, Grid } from '@material-ui/core';
import { ToggleButtonGroup, ToggleButton } from '@material-ui/lab';

// Helper
import { RecipientBookContext } from '../../Context/RecipientBookContext';
import { additionalOccasionsList, unmaskPhoneNumber } from '../../Helpers/Helpers';
import { isEmpty } from '../../../../../../../../pages/Account/utils/object';
import { getUserProfile } from '../../../../../../../../../state/ducks/Checkout/ducks/Common/Common-Selectors';
import { getAccessTokenSafely } from '../../../../../../../../../state/ducks/Member/ducks/Auth/Plugin/auth/auth';

// Icons
import expoandMoreIcon from '../../Assets/expandMoreIcon.svg';

// API
import { processAddOccasionEntries } from '../../../../../../../../../apis/account-apis/RecipientBook-apis/processAddOccasionEntries';
import { trackEvent } from '../../../../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { getBrand } from '../../../../../../../../../state/ducks/App/ducks/Brand/Brand-Selectors';

// Component
import AddOccasionsForm from '../../../Common/AddNewRecipient/AddOccasions/AddOccasionsForm';

const AdditionalOccasions = ({ classes, rcpId, occasions, parentComponent, rcpFirstName, rcpLastName, getStatusOfSwitch, switchStatus, reminderPhone, occasionsList }) => {
    const [selectedOccasion, setSelectedOccasion] = useState({});
    const [filteredAdditionalOccasions, setFilteredAdditionalOccasions] = useState([]);
    const [holidayOccasionError, setHolidayOccasionError] = useState('');
    const [loading, setLoading] = useState(false);

    const dispatch = useDispatch();
    const userProfile = useSelector(getUserProfile) || {};
    const brandId = useSelector(getBrand).id;
    const isMobile = useSelector(getSSRDeviceType) === 'mobile';

    const { ContactId, firstName, lastName } = userProfile || '';
    const occasionBubbleRef = useRef();
    const prevOccasionsRef = useRef(null);

    const methods = useFormContext();
    const { currentRecipient, setCurrentRecipient, setSelectedOccasionToEdit, showMoreOccasions,
        setShowMoreOccasions, setIsProfileEditing, setIsAddressEditing, setAddNewAddressClicked, setSelectedAddressToEdit, setIsOccasionsEditing,
    } = useContext(RecipientBookContext);

    const findSelectedIndex = methods.watch('occasions')?.findIndex((occ) => occ.occasion === selectedOccasion.occasion)
    // Below two line of code used for add new recipient page
    const uniqueOccasions = [...new Set(occasions?.map(item => item.occasion))];
    const filteredAdditionalOccasionsAddForm = additionalOccasionsList?.filter(item => !uniqueOccasions.includes(item.occasion));
    // Below two line of code used for edit recipient page
    const uniqueOccasionsList = [...new Set(occasionsList?.map(item => item.occasion))];

    const isHolidayOccasion = (occ) => (Number(occ?.occasion) >= 5 && Number(occ?.occasion) <= 21);
    
    const sortOccasionsByDesc = (occasions) => {
        // Check if the array is empty
        if (occasions.length === 0) {
            return [];
        }
        // Clone the array to avoid mutating the original array
        const sortedList = [...occasions];
        
        // Sort the array based on the 'desc' property
        sortedList.sort((a, b) => a.desc.localeCompare(b.desc));
        
        // Return sorted array
        return sortedList;
    };

    // Determine which set of occasions to use (Add Form or Edit Form)
    const newoccasions = (parentComponent !== 'DesktopAddNewRecipient' && parentComponent !== 'AddNewRecipient') ? sortOccasionsByDesc(filteredAdditionalOccasions) : sortOccasionsByDesc(filteredAdditionalOccasionsAddForm);
    const slicedOccasionsByDeviceType = isMobile ? newoccasions?.slice(0, 6) : newoccasions.slice(0, 12) || [];
    const displayOccasions = showMoreOccasions ? newoccasions : sortOccasionsByDesc(slicedOccasionsByDeviceType);
    
    useEffect(() => {
        if (Array.isArray(uniqueOccasionsList)) {
            const currentOccasionListSorted = sortOccasionsByDesc(additionalOccasionsList?.filter(item => !uniqueOccasionsList.includes(item.occasion)));
            setFilteredAdditionalOccasions(currentOccasionListSorted);
        };

        return () => setShowMoreOccasions(false);
    }, []);

    useEffect(() => {
        if (prevOccasionsRef.current !== null) {
            if (JSON.stringify(prevOccasionsRef.current) !== JSON.stringify(occasionsList)) {
                const sortedOccasions = sortOccasionsByDesc(additionalOccasionsList?.filter(item => !uniqueOccasionsList.includes(item.occasion)));
                setFilteredAdditionalOccasions(sortedOccasions);
            }
        }
        prevOccasionsRef.current = occasionsList;
    }, [occasionsList]);
    

    useEffect(() => {
        if (!showMoreOccasions) {
            const resetSelectedOccasion = occasions.filter((occ) => occ.occasion !== selectedOccasion.occasion);
            methods.setValue('occasions', [...resetSelectedOccasion]);
            setSelectedOccasion({}); // reset selected occasion on closing show more occasions
        };
    }, [showMoreOccasions]);


    const handleSaveAdditionalOccasions = (occasionId, reminderPhoneNum) => {
        const newSelectedOccasion = methods.watch('occasions').find((occ) => occ.occasion === occasionId) || methods.watch('occasions').find((occ) => occ.occasion === occasionId);
        const updatedNewSelectedOccasion = { ...newSelectedOccasion, reminderPhone: reminderPhoneNum || reminderPhone || '' };
        if (!occasions.some((occ) => Number(occ.occasion) === occasionId) && (parentComponent !== 'DesktopAddNewRecipient' && parentComponent !== 'AddNewRecipient')) {
            return postOccasionEntries(updatedNewSelectedOccasion);
        }
    };

    const addOccasionToRecipient = (newOccasion) => {
        if (!isEmpty(currentRecipient)) {
            if (currentRecipient.occasions?.length) {
                currentRecipient.occasions = [...currentRecipient.occasions, newOccasion]
            } else {
                currentRecipient['occasions'] = [newOccasion]
            }
            currentRecipient.occasions.map((occasion) => occasion.reminderPhone = newOccasion.reminderPhone);
            setCurrentRecipient(currentRecipient)
        }

        methods.setValue('occasions', [...currentRecipient.occasions]);
        setFilteredAdditionalOccasions(additionalOccasionsList?.filter(item => !uniqueOccasions.includes(item.occasion)));
    };


    const postOccasionEntriesMutation = useMutation({
        mutationFn: processAddOccasionEntries,
        onSuccess: (data) => {
            if (data.status === 200 && data && data.data !== undefined) {
                const newOccasion = data.data[0];
                addOccasionToRecipient(newOccasion);
            }
            setLoading(false);
            setSelectedOccasion({});
            setShowMoreOccasions(false);
            methods.reset({}, { keepValues: true, keepDirty: false });
        },
        onError: (err) => {
            setHolidayOccasionError('Something went wrong. Please try again.');
            setLoading(false);
            mbpLogger.logError({
                function: 'postSingleOccasionEntry',
                appName: process.env.npm_package_name,
                module: 'AdditionalOccasions.js',
                message: `Error While Adding Holiday Occasion for Recipient with Contact ID: ${userProfile?.ContactId}`,
                err,
            });
        },
    });

    const postOccasionEntries = async (holidayOccasion) => {
        setLoading(true);
        dispatch(trackEvent({
            eventName: "contact_edit_interaction",
            edit_action_type: "Edit Occasion",
            occasion: holidayOccasion.desc,
            eventCategory: "Address Book",
            eventAction: "Contact Edit",
            eventLabel: `Occasion Add - ${holidayOccasion.desc}`
        }))
        const jwtToken = await getAccessTokenSafely();
        const {
            desc,
            occasion,
            reminderPhone,
            subscribedPhone,
            subscribedEmail,
        } = holidayOccasion

        const occasionPayload = [
            {
                ...holidayOccasion,
                rcpFirstName,
                rcpLastName,
                custFirstName: firstName,
                custLastName: lastName,
                occasion,
                desc,
                rcpId,
                createdUser: "SSP",
                reminderPhone: unmaskPhoneNumber(reminderPhone || ''),
                subscribedPhone: subscribedPhone || false,
                brandId,
                // Subscribe new holiday reminders to email notifications by default
                reminderEmail: userProfile.email,
                subscribedEmail: subscribedEmail || false,
            }
        ]


        const holidayOccasionPayload = [
            {
                rcpFirstName,
                rcpLastName,
                custFirstName: firstName,
                custLastName: lastName,
                occasion,
                desc,
                rcpId,
                createdUser: "SSP",
                reminderPhone: unmaskPhoneNumber(reminderPhone || ''),
                subscribedPhone: subscribedPhone || false,
                brandId,
                // Subscribe new holiday reminders to email notifications by default
                reminderEmail: userProfile.email,
                subscribedEmail: subscribedEmail || false,
            }
        ]

        postOccasionEntriesMutation.mutate({
            ContactId,
            data: Number(holidayOccasion.occasion) < 5 ? occasionPayload : holidayOccasionPayload,
            jwtToken,
        });
    };

    const handleOccasionSelection = (occasionId) => {
        const newSelectedOccasion = additionalOccasionsList.find((occ) => occ.occasion === occasionId);
        const updatedNewSelectedOccasion = { ...newSelectedOccasion, reminderPhone: reminderPhone || '' };

        // Handle Adding Occasions on Add New Recipient screen
        if (parentComponent === 'DesktopAddNewRecipient' || parentComponent === 'AddNewRecipient') {
            return methods.setValue('occasions', [...methods.watch('occasions'), updatedNewSelectedOccasion]);
        }

        const filterOutEmptyOccasion = methods.watch('occasions').filter((occ) => occ.occasion !== selectedOccasion.occasion)
        methods.setValue('occasions', [...filterOutEmptyOccasion, updatedNewSelectedOccasion]);
        setSelectedOccasion(updatedNewSelectedOccasion);
        setShowMoreOccasions(true);
        setIsProfileEditing(false); // setting global profile state true to keep edit profile form open
        setIsAddressEditing(false);
        setAddNewAddressClicked(false);
        setSelectedAddressToEdit('');
        setSelectedOccasionToEdit('');
        setIsOccasionsEditing(false);
    };

    const onSubmit = (data) => {
        const { occasion, reminderPhone } = data.occasions[findSelectedIndex];
        handleSaveAdditionalOccasions(occasion, reminderPhone);
    }

    const onError = (err) => {
        console.log('errr', err);
    }

    return (
        <div>
            {holidayOccasionError && <div className={classes.errorStyling} style={{ textAlign: 'center' }}><p>There is an issue adding this occasion. <br />Please try again</p></div>}
            {filteredAdditionalOccasions && filteredAdditionalOccasions?.length > 0 && (
                <>
                    <p className={classes.mediumTitle}>Add Additional Reminders</p>
                    <div className={`${classes.additionalOccasionsBtn} ${!showMoreOccasions ? classes.showLess : ''}`}>
                    <ToggleButtonGroup
                        orientation="vertical"
                        exclusive
                        onChange={(_, occasionId) => {
                            handleOccasionSelection(occasionId);
                        }}
                        fullWidth
                        ref={occasionBubbleRef}
                    >
                        {displayOccasions?.map((occasion) => (
                            <ToggleButton
                                classes={{ selected: classes.selected }}
                                selected={selectedOccasion.occasion === occasion.occasion}
                                disableRipple
                                style={{ height: 'max-content' }}
                                key={occasion.desc}
                                disabled={loading}
                                value={occasion.occasion}
                                aria-label={occasion.desc}
                            >
                                {occasion.desc}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                        {/* Show form for non holiday occasions only */}
                        {(occasions.find((occ) => occ.occasion === selectedOccasion.occasion) && (parentComponent !== 'DesktopAddNewRecipient' && parentComponent !== 'AddNewRecipient') && showMoreOccasions) &&
                            <>
                                <AddOccasionsForm 
                                    key={occasions}
                                    classes={classes}
                                    occasion={methods.watch('occasions')[findSelectedIndex]}
                                    showDivider={false}
                                    methods={methods}
                                    index={findSelectedIndex}
                                    holidayOccasion={isHolidayOccasion(methods.watch('occasions')[findSelectedIndex])} 
                                    parentComponent={parentComponent} 
                                    rcpFirstName={rcpFirstName} 
                                    rcpLastName={rcpLastName} 
                                    getStatusOfSwitch={getStatusOfSwitch}
                                    switchStatus={switchStatus}
                                />
                                <Grid container direction="column" justifyContent="center" alignItems="center">
                                    <Grid item xs={12} md={4} lg={4}></Grid>
                                    <Grid item xs={12} md={4} lg={4} style={{ width: '100%' }}>
                                        <Button
                                            fullWidth
                                            variant='contained'
                                            className={classes.commonBtnContained}
                                            onClick={
                                                methods.handleSubmit(onSubmit, onError)
                                            }
                                            disabled={loading}
                                            style={{ margin: '16px 0px 0px 0px', height: '46px', width: '100%' }}
                                        >
                                            {loading ? <CircularProgress /> : 'Save'}
                                        </Button>
                                    </Grid>
                                    <Grid item xs={12} lg={4}></Grid>
                                </Grid>
                            </>
                    }
                    <div role="button" onKeyDown={() => {}} tabIndex={0} className={classes.expandMoreSection} onClick={() => setShowMoreOccasions(!showMoreOccasions)}>
                        <div className={(!showMoreOccasions) ? classes.expandMoreSectionOverlay : ''} />
                        <img src={expoandMoreIcon} alt='expand more icon' className={showMoreOccasions ? classes.rotateIcon : ''} />
                    </div>
                </div>
            </>
            )}
        </div>
    );
};

AdditionalOccasions.propTypes = {
    rcpId: string,
    switchStatus: bool,
    rcpLastName: string,
    rcpFirstName: string,
    occasionsList: arrayOf(PropTypes.shape({
        desc: string,
        rcpId: string,
        dateDD: string,
        dateMM: string,
        dateYYYY: string,
        subscribed: bool,
        occasion: string,
        occasionId: string,
        createdDate: string,
        createdUser: string,
        reminderEmail: string,
        reminderPhone: string,
        subscribedEmail: bool,
        subscribedPhone: bool,
    })),
    reminderPhone: string,
    parentComponent: string,
    getStatusOfSwitch: func,
    classes: object.isRequired,
    occasions: arrayOf(PropTypes.shape({
        desc: string,
        rcpId: string,
        dateDD: string,
        dateMM: string,
        dateYYYY: string,
        subscribed: bool,
        occasion: string,
        occasionId: string,
        createdDate: string,
        createdUser: string,
        reminderEmail: string,
        reminderPhone: string,
        subscribedEmail: bool,
        subscribedPhone: bool,
    })),
};

AdditionalOccasions.defaultProps = {
    rcpId: '',
    switchStatus: false,
    rcpLastName: '',
    rcpFirstName: '',
    occasionsList: [],
    parentComponent: '',
    getStatusOfSwitch: () => {},
};

export default AdditionalOccasions;
